/**
 * AddCreditCardCtrl controller
 * Add/edit subscribers
 */

import moment from 'moment'
import { US_STATES, CA_STATES, GET_COUNTRIES, GEOAPIFY_CONFIDENCE_THRESHOLD } from 'app/core/constants'
import { geoapifyAPI } from '../../../app/helpers/geoapify'

/**
 * Org Address Validation Service: missing setting defaults Allow.
 * Disallow is false / 'false' / 0 / '0' on { value }, or a bare extra?activation string.
 */
function isAddressValidationSettingAllowed (setting) {
  if (setting == null) return true
  const raw = typeof setting === 'object' ? setting.value : setting
  return raw !== false && raw !== 'false' && raw !== 0 && raw !== '0'
}

/**
 * AddCreditCardCtrl controller
 *
 * @class AddCreditCardCtrl
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 *
 * @ngdoc controller
 * @name mwApp/subscribers.controller:AddCreditCardCtrl
 */
/* @ngInject */
export default function AddCreditCardCtrl ($scope, $uibModalInstance, $sce, $location, CreditCard, NetworkSetting, authSvc, card, alertLiteSvc, alertSvc) {
  var vm = this

  $scope.$watch('vm.card.storageType', (newValue) => {
    if (newValue === 'TELR') { // Note: disable credit card details for TELR
      vm.card.expirationDate = null
      vm.card.creditCardNumber = ''
    }
  })

  /**
   * Url for iframe
   *
   * @type {object | string}
   */
  vm.externalUrl = null

  /**
   * Loader indicator
   *
   * @type {boolean}
   */
  $scope.loading = false

  vm.resizeIframe = function () {
    $scope.loading = false
  }

  /**
   * Credit Card
   *
   * @memberof mwApp/subscribers.controller:AddCreditCardCtrl
   * @type {models.CreditCard}
   */
  vm.card = card

  // MW-12853 / MW-13104: Geoapify only when FEATURE_GEOAPIFY_ENABLED (first gate) AND the
  // org ADDRESS_VALIDATION_SERVICE_ENABLED is not Disallow. Missing setting defaults Allow.
  // Disallow: false, 'false', 0, '0' on { value }, or a bare extra?activation string.
  // Stay false until loadAll resolves so Disallow orgs never call Geoapify while settings are pending.
  vm.addressValidationAllowed = false
  const geoapifyFlagEnabled = authSvc.hasEnabledFlag('FEATURE_GEOAPIFY_ENABLED')
  const cardNetworkId = card.subscriber && card.subscriber.network && card.subscriber.network.id
  if (geoapifyFlagEnabled && cardNetworkId) {
    NetworkSetting.loadAll({ network: { id: cardNetworkId } })
      .then(settings => {
        const setting = settings && settings.ADDRESS_VALIDATION_SERVICE_ENABLED
        vm.addressValidationAllowed = geoapifyFlagEnabled && isAddressValidationSettingAllowed(setting)
      })
      .catch(() => { vm.addressValidationAllowed = false })
  }

  // Note: disabled customBillingAddress for TELR
  if (card.storageType === 'TELR') {
    card.customBillingAddress = false
  }

  /**
   * isNew flag
   *
   * @memberof mwApp/subscribers.controller:AddCreditCardCtrl
   * @type {Boolean}
   */
  vm.isNew = card._isNew()

  vm.statesUS = US_STATES
  vm.statesCanada = CA_STATES

  GET_COUNTRIES().to(vm.countries = [])

  // update billing address
  card._fillAddress()
  vm.$customCard = card._clone()
  vm.$billingCard = card._clone()

  // security types
  vm.securityTypes = [
    {
      name: 'Authorize.net - AIM',
      value: 'AUTHORIZE_NET'
    }, {
      name: 'Local System',
      value: 'LOCAL'
    },
    {
      name: 'Telr',
      value: 'TELR'
    },
    {
      name: 'Gr4vy',
      value: 'GR4VY_GATEWAY'
    },
    {
      name: '2C2P',
      value: 'CCPP'
    }
  ]

  // set default storage type for new credit card, from extra
  if (vm.isNew) {
    CreditCard.loadExtra(CreditCard.extraFor(card))
      .then(extra => {
        vm.securityTypes = vm.securityTypes.filter(st => {
          return extra.allowedStorageTypes.includes(st.value)
        })
        card.storageType = extra.defaultStorageType
      })
  }

  vm.isDateValid = function () {
    return !vm.isNew || moment({
      year: card.expirationDate.expirationYear,
      month: +card.expirationDate.expirationMonth - 1
    }).endOf('month').isAfter()
  }

  vm.changeBillingAddress = function () {
    if (!card.customBillingAddress) {
      Object.keys(vm.card).forEach((key) => {
        vm.$customCard[key] = vm.card[key]
      })
    }
    card._fillAddress(card.customBillingAddress ? vm.$customCard : null)
  }

  /**
   * Create or edit credit card
   *
   * @memberof mwApp/subscribers.controller:AddCreditCardCtrl
   */
  vm.submit = function () {
    vm.setDisable()
    var subscriberCloned = card.subscriber._clone()
    var isNew = card._isNew()

    const doSave = () => card._save((card) => {
      card.subscriber = null

      card.redirectUrl = `${$location.absUrl()}${!$location.hash() ? '#cards' : ''}` // Note: generate redirect url for telr #${$location.hash()}
      delete card.user
    }, null, null).data()
      .then(
        (data) => {
          const { externalUrl } = data
          if (externalUrl) {
            vm.externalUrl = $sce.trustAsResourceUrl(externalUrl)
            $scope.loading = true
          }

          card.subscriber = subscriberCloned
          card._url = null
          return card._update()
        }
      )
      .then(
        () => {
          if (!vm.externalUrl) {
            $uibModalInstance.close(card)
            $scope.loading = false
          }

          if (isNew && card.status === 'OK') {
            alertLiteSvc.alert(card.name + ' Credit Card added', 'success')
          }
        }
      )
      .catch(
        () => {
          card.subscriber = subscriberCloned
        }
      )
      .finally(vm.unsetDisable)

    vm.validateAddress()
      .then(doSave)
      .finally(vm.unsetDisable)
  }

  /**
   * Cancel editing, close modal window
   *
   * @memberof mwApp/settings.controller:AddExtensionCtrl
   */
  vm.cancel = function () {
    $uibModalInstance.dismiss('cancel')
  }

  vm.onCountrySelect = function () {
    vm.card.state = ''
  }

  vm.validateAddress = function () {
    if (!vm.addressValidationAllowed) return Promise.resolve()

    const text = `&text=${vm.card.address?.replace(/ /g, '%20') || ''}`
    const state = vm.card.state?.replace(/ /g, '%20')
    const stateParam = state ? `&state=${vm.card.state?.replace(/ /g, '%20')}` : ''
    const city = `&city=${vm.card.city || ''}`
    const postCode = `&postcode=${vm.card.zipcode || ''}`
    const country = `&country=${vm.countries.find(c => c.code === vm.card.country)?.name || ''}`

    const searchString = postCode + country + text + city + stateParam

    return geoapifyAPI.validateAddress(searchString, alertSvc)
  }

  vm.getFormattedStreet = function (item) {
    return [item?.properties?.street, item?.properties?.housenumber].filter(Boolean).join(', ')
  }

  vm.addressChanged = function () {
    if (!vm.addressValidationAllowed) return []
    const street = vm.card.address?.replace(/ /g, '%20')
    if (!street) return

    return geoapifyAPI.findAddresses(street, { countryCode: vm.card.country, filter: 'street' })
  }

  vm.cityChanged = function () {
    if (!vm.addressValidationAllowed) return []
    const city = vm.card.city?.replace(/ /g, '%20')
    if (!city) return

    return geoapifyAPI.findAddresses(city, { countryCode: vm.card.country, filter: 'city' })
  }

  vm.onCitySelect = function (item) {
    vm.card.zipcode = item.properties.postcode

    if (vm.card.country !== 'US' && vm.card.country !== 'CA') {
      vm.card.state = item.properties.state
    }
  }

  vm.getFormattedPostcode = function (item) {
    return [item?.properties?.postcode, item?.properties?.city].filter(Boolean).join(', ')
  }

  vm.zipcodeChanged = function () {
    if (!vm.addressValidationAllowed) return []
    const postCode = vm.card.zipcode?.replace(/ /g, '%20')
    if (!postCode) return

    return geoapifyAPI.findAddresses(postCode, { countryCode: vm.card.country, filter: 'postcode' })
  }

  vm.onPostcodeSelect = function (item) {
    vm.card.zipcode = item.properties.postcode
  }

  vm.stateChanged = function () {
    if (!vm.addressValidationAllowed) return []
    const state = vm.card.state?.replace(/ /g, '%20')
    if (!state) return

    return geoapifyAPI.findAddresses(state, { countryCode: vm.card.country, filter: 'state' })
  }
}
